import React, { useState, useEffect } from 'react';
import AdminCategories from '../components/admin/AdminCategories';
import AdminQuestions from '../components/admin/AdminQuestions';
import AdminSettings from '../components/admin/AdminSettings';
import AdminStats from '../components/admin/AdminStats';
import { questionService } from '../firebase/questionService';
import type { Question, Category } from '../types/game';

const AdminDashboard: React.FC = (): JSX.Element => {
  const [tab, setTab] = useState<'categories' | 'questions' | 'settings' | 'stats'>('categories');
  const [stats, setStats] = useState<{
    totalQuestions: number;
    activeQuestions: number;
    totalCategories: number;
    activeCategories: number;
    mostUsedQuestions: Question[];
    topRatedQuestions: Question[];
    difficultyDistribution: {
      easy: number;
      medium: number;
      hard: number;
    };
    categoryStats: {
      [categoryId: string]: {
        name: string;
        totalQuestions: number;
        averageSuccessRate: number;
        totalCorrect: number;
        totalIncorrect: number;
      };
    };
  }>({
    totalQuestions: 0,
    activeQuestions: 0,
    totalCategories: 0,
    activeCategories: 0,
    mostUsedQuestions: [],
    topRatedQuestions: [],
    difficultyDistribution: {
      easy: 0,
      medium: 0,
      hard: 0,
    },
    categoryStats: {},
  });

  useEffect(() => {
    if (tab === 'stats') {
      fetchStats();
    }
  }, [tab]);

  const fetchStats = async () => {
    const questions = await questionService.getQuestions();
    const activeQuestions = questions.filter(q => q.isActive !== false);
    const categories = await questionService.getCategories();
    const activeCategories = categories.filter(c => c.isActive !== false);

    // Calculate difficulty distribution
    const difficultyDistribution = {
      easy: questions.filter(q => q.difficulty === 'easy').length,
      medium: questions.filter(q => q.difficulty === 'medium').length,
      hard: questions.filter(q => q.difficulty === 'hard').length,
    };

    // Calculate category statistics
    const categoryStats: { [key: string]: any } = {};
    categories.forEach(category => {
      const categoryQuestions = questions.filter(q => q.categoryId === category.id);
      const totalCorrect = categoryQuestions.reduce((sum, q) => sum + (q.correctAnswers || 0), 0);
      const totalIncorrect = categoryQuestions.reduce((sum, q) => sum + (q.incorrectAnswers || 0), 0);
      const totalAnswers = totalCorrect + totalIncorrect;
      
      categoryStats[category.id] = {
        name: category.name,
        totalQuestions: categoryQuestions.length,
        averageSuccessRate: totalAnswers > 0 ? (totalCorrect / totalAnswers) * 100 : 0,
        totalCorrect,
        totalIncorrect,
      };
    });

    // Get most used questions (increased to 10)
    const mostUsedQuestions = [...questions]
      .sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0))
      .slice(0, 10);

    // Get top rated questions (increased to 10)
    const topRatedQuestions = [...questions]
      .filter(q => (q.ratingCount || 0) > 0)
      .sort((a, b) => (b.rating || 0) - (a.rating || 0))
      .slice(0, 10);

    setStats({
      totalQuestions: questions.length,
      activeQuestions: activeQuestions.length,
      totalCategories: categories.length,
      activeCategories: activeCategories.length,
      mostUsedQuestions,
      topRatedQuestions,
      difficultyDistribution,
      categoryStats,
    });
  };

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-extrabold text-brand-yellow mb-8 text-center">Admin Dashboard</h1>
        <div className="flex gap-4 mb-8 justify-center">
          <button
            className={`px-6 py-3 rounded-lg font-bold text-lg ${tab === 'categories' ? 'bg-brand-yellow text-black' : 'bg-[#222] text-white'}`}
            onClick={() => setTab('categories')}
          >
            Categories
          </button>
          <button
            className={`px-6 py-3 rounded-lg font-bold text-lg ${tab === 'questions' ? 'bg-brand-yellow text-black' : 'bg-[#222] text-white'}`}
            onClick={() => setTab('questions')}
          >
            Questions
          </button>
          <button
            className={`px-6 py-3 rounded-lg font-bold text-lg ${tab === 'settings' ? 'bg-brand-yellow text-black' : 'bg-[#222] text-white'}`}
            onClick={() => setTab('settings')}
          >
            Settings
          </button>
          <button
            className={`px-6 py-3 rounded-lg font-bold text-lg ${tab === 'stats' ? 'bg-brand-yellow text-black' : 'bg-[#222] text-white'}`}
            onClick={() => setTab('stats')}
          >
            Statistics
          </button>
        </div>
        <div className="bg-[#222] rounded-xl p-8 shadow-2xl min-h-[400px]">
          {tab === 'categories' && <AdminCategories />}
          {tab === 'questions' && <AdminQuestions />}
          {tab === 'settings' && <AdminSettings />}
          {tab === 'stats' && <AdminStats stats={stats} />}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 